import { cn } from '@/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

const Dialog: React.FC<
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>
> = (props) => <DialogPrimitive.Root {...props} modal={false} />;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = (props: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
);

DialogPortal.displayName = DialogPrimitive.Portal.displayName;

const classes = {
  Overlay:
    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[250] bg-gray-600/80 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in'
};

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(classes.Overlay, className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  overlayProps?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;
  customOverlay?: boolean;
  onClose?: () => void;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, customOverlay = true, onClose, ...props }, ref) => {
  const content = (
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[30%] left-[50%] z-[250] grid w-full max-w-2xl translate-x-[-50%] translate-y-[-30%] gap-4 overflow-auto border bg-white p-6 shadow-lg duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in sm:rounded-lg md:w-full',
        className
      )}
      onPointerDown={(e) => {
        // If this is a scrollbar click, prevent closing inputs inside
        if (isScrollbarClick(e)) {
          e.preventDefault();
        }

        props.onPointerDown?.(e);
      }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  );

  if (customOverlay) {
    return (
      <DialogPortal>
        <div
          {...props.overlayProps}
          onClick={onClose}
          className={cn(classes.Overlay, props.overlayProps?.className)}
        />
        {content}
      </DialogPortal>
    );
  }

  return (
    <DialogPortal>
      <DialogOverlay>{content}</DialogOverlay>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

export type DialogFooterProps = React.HTMLAttributes<HTMLDivElement>;

const DialogFooter = ({ className, ...props }: DialogFooterProps) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription
};

const isScrollbarClick = (
  event: React.PointerEvent<HTMLDivElement>
): boolean => {
  // Get the element under the pointer
  const element = document.elementFromPoint(
    event.clientX,
    event.clientY
  ) as HTMLElement;
  if (!element) return false;

  const scrollbarWidth = 20;

  // Check if element has a scrollbar
  const hasVerticalScrollbar = element.scrollHeight > element.clientHeight;
  const hasHorizontalScrollbar = element.scrollWidth > element.clientWidth;

  if (hasVerticalScrollbar || hasHorizontalScrollbar) {
    const rect = element.getBoundingClientRect();

    // Check if the click is on the vertical scrollbar (usually on the right)
    const onVerticalScrollbar =
      hasVerticalScrollbar &&
      event.clientX > rect.right - scrollbarWidth &&
      event.clientX <= rect.right;

    // Check if the click is on the horizontal scrollbar (usually at the bottom)
    const onHorizontalScrollbar =
      hasHorizontalScrollbar &&
      event.clientY > rect.bottom - scrollbarWidth &&
      event.clientY <= rect.bottom;

    if (onVerticalScrollbar || onHorizontalScrollbar) {
      return true;
    }
  }

  return false;
};
